import { DotLegalCheckbox, DotLegalDialog, DotLegalHeader, DotLegalSelect } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import React from "react";
import { Box, Collapse } from "@mui/material";
import useLegalEntityThirdCountryDialogHooks from "./LegalEntityThirdCountryDialog.hooks";
import { LegalEntityThirdCountrySettingsViewModel } from "./LegalEntityThirdCountryDialog.types";
import { LevelOfProtectionType } from "../addLegalEntityDialog/AddLegalEntityDialog.types";
import { filterTransferBasis } from "../../legalBasis/TransferBasis.Helper.ts";

export interface ILegalEntityThirdCountryDialog {
    thirdCountrySettings: LegalEntityThirdCountrySettingsViewModel;
    legalEntityId: string;
    onDialogClose: () => void;
    onSaved: () => void;
}

function LegalEntityThirdCountryDialog(props: ILegalEntityThirdCountryDialog) {
    const { translateString } = useTranslation();

    const { LevelOfProtections, viewModel, updateSaveModel, onSaveClick, isSaving, transferBasisIsLoading, transferBases } =
        useLegalEntityThirdCountryDialogHooks(props);

    function getSelectedLevelOfProtection() {
        if (viewModel.levelOfProtection === LevelOfProtectionType.AdequateProtection) {
            return LevelOfProtectionType.CPIEDA;
        }

        return viewModel.levelOfProtection;
    }

    const usesDataPrivacyFramework = viewModel.levelOfProtection === LevelOfProtectionType.DataPrivacyFramework;

    return (
        <DotLegalDialog
            size="sm"
            header={translateString("thirdCountrySettings")}
            buttonOkText={translateString("save")}
            open
            onDialogClose={props.onDialogClose}
            onOkClick={() => onSaveClick()}
            inProgress={isSaving}
            disableBackdropOnCloseIfValueChange={viewModel}
            isLoading={transferBasisIsLoading}
        >
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <DotLegalHeader center fontWeight={400} headerStyle="small">
                    {translateString("thirdCountrySettingsContent", { country: props.thirdCountrySettings.country })}
                </DotLegalHeader>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Box sx={{ flexDirection: "column", minWidth: 350 }}>
                    <DotLegalCheckbox
                        checked={viewModel.isTIAUnnecessary}
                        onChange={updateSaveModel.onIsTIAUnnecessaryChange}
                        label={translateString("tiaIsUnnecessary")}
                    />

                    <DotLegalCheckbox
                        checked={viewModel.isTransferBasisUnnecessary}
                        onChange={updateSaveModel.onIsTransferBasisUnnecessaryChange}
                        label={translateString("transferBasisNotRequired")}
                    />
                    {(props.thirdCountrySettings.countryCode === "US" || props.thirdCountrySettings.countryCode === "CA") && (
                        <React.Fragment>
                            <DotLegalSelect
                                label={translateString("levelOfProtection")}
                                options={LevelOfProtections}
                                placeholder={translateString("levelOfProtection")}
                                selectedItem={LevelOfProtectionType[getSelectedLevelOfProtection()]}
                                onChange={(item) => updateSaveModel.onLevelOfProtectionChange(item!)}
                                noOptionsLabel={translateString("noOptions")}
                                disableClearable
                            />
                        </React.Fragment>
                    )}

                    {!viewModel.isTransferBasisUnnecessary && viewModel.levelOfProtection !== LevelOfProtectionType.AdequateProtection && (
                        <DotLegalSelect
                            disabled={usesDataPrivacyFramework}
                            selectedItem={viewModel.transferBasisId}
                            isLoading={transferBasisIsLoading}
                            label={translateString("transferBasis")}
                            placeholder={translateString("transferBasis")}
                            options={filterTransferBasis(transferBases ?? [], props.thirdCountrySettings.countryCode)}
                            onChange={updateSaveModel.onTransferBasisChange}
                            noOptionsLabel={translateString("noOptions")}
                        />
                    )}

                    <Collapse in={props.thirdCountrySettings.transferBasisId !== viewModel.transferBasisId && viewModel.transferBasisId !== undefined}>
                        <DotLegalCheckbox
                            checked={viewModel.addTransferBasisToSharings}
                            onChange={updateSaveModel.onAddDataPrivacyFrameworkToSharingsChange}
                            label={translateString("transferTransferBasis")}
                            tooltip={translateString("transferTransferBasisToSharings")}
                        />
                    </Collapse>
                </Box>
            </Box>
        </DotLegalDialog>
    );
}

export default LegalEntityThirdCountryDialog;
